import type { IPlace } from "@bbj-types/address";
import type { IContact } from "@bbj-types/contact";
import type { ContactType } from "@bbj-types/contact/enum";
import type { Override } from "@bbj-types/utils";
import type { UserPartnerAccess } from "@root/utils/enums/structurePartner";
import { useCallback } from "react";
import { axiosEnterprise } from "./axios";

type BaseContactBody = Pick<
  IContact,
  "firstName" | "lastName" | "job" | "email" | "phone" | "description" | "isTemp" | "user"
>;

type BaseContactStructurePartnerBody = Override<
  Pick<IContact, "firstName" | "lastName" | "job" | "email" | "phone" | "description" | "isTemp" | "user">,
  {
    email: string | undefined;
    phone: string | undefined;
    job: string | undefined;
    description: string | undefined;
  }
>;

export type TCreateContactBody<T extends ContactType> = T extends ContactType.ENTERPRISE
  ? BaseContactBody & { type: T; enterprise: string; place: IPlace }
  : T extends ContactType.STRUCTURE_PARTNER
    ? BaseContactStructurePartnerBody & {
        type: T;
        userAccess: UserPartnerAccess;
        place?: IPlace;
        structurePartner: string;
      }
    : never;

export type TContactAuthor = Override<
  IContact,
  { author?: { lastName: string; firstName: string; createdAt: string; updatedAt: string } }
>;

export const useCreateContact = () => {
  const [state, execute, cancel] = axiosEnterprise<TContactAuthor, TCreateContactBody<ContactType>>(
    {
      url: "/contacts",
      method: "post",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: TCreateContactBody<ContactType>) => {
      return execute({
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
